/* Shared focus-trap helpers used by Modal.
   Filtered at query time (see tabbables) to drop hidden / aria-hidden /
   inert nodes that technically match the selector but would trap focus
   on an invisible element. */

export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  /* Rich-text editors (TipTap, Lexical, ProseMirror) mount as a
     contenteditable host inside the modal body. Without this
     selector entry the focus trap skips them and Tab leaves
     the modal. The attribute is tabbable per HTML spec when
     non-empty; [contenteditable="false"] stays filtered. */
  '[contenteditable]:not([contenteditable="false"])',
  /* Media controls with tabIndex-equivalent interactive UI. */
  'audio[controls]',
  'video[controls]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/* An element is a valid trap stop only if it's actually rendered and
   reachable. offsetParent is null for display:none and display:none
   ancestors; visibility:hidden is caught by the getComputedStyle
   check below. Cheaper checks run first so the style read only
   happens on the candidates we'd otherwise consider real stops. */
export function isFocusable(el: HTMLElement): boolean {
  /* :disabled covers form controls inside a <fieldset disabled> that
     inherit their disabled state. A plain hasAttribute('disabled')
     check misses those — per spec they're not focusable but slip
     through. closest('fieldset:disabled') is the backup path for
     browsers that don't resolve :disabled against inherited state
     reliably. */
  if (el.matches(':disabled')) return false;
  if (el.closest('fieldset:disabled')) return false;
  if (el.hasAttribute('hidden')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if (el.closest('[inert]')) return false;
  const cs = getComputedStyle(el);
  if (cs.visibility === 'hidden' || cs.display === 'none') return false;
  /* offsetParent is null for display:none (caught above) AND for
     position:fixed elements. A fixed-positioned button inside the
     modal — a floating action button, a positioned toolbar — is
     still visible and tabbable, so special-case it: admit the
     element if computed position is fixed, otherwise fall through
     to the offsetParent rule. */
  if (el.tagName !== 'BODY' && el.offsetParent === null && cs.position !== 'fixed') return false;
  return true;
}

export function tabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusable);
}
